/**
 * The Approvals tab — Step 47.
 *
 * Shows every chain on the ticket, stage by stage, and lets whoever is being
 * asked answer without leaving the ticket they are approving.
 *
 * The stage list is drawn even for stages nobody can act on yet, deliberately:
 * *"why is this still waiting?"* is the question an approvals UI exists to
 * answer, and a panel that shows only the open stage answers it with silence.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, CircleSlash, Clock, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select } from '../../components/ui/select';
import { TokenSelect } from '../../components/ui/tokenselect';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { fullTime } from '../../lib/time';
import { useAuth } from '../../lib/AuthContext';
import { ticketsApi } from './api';
import { cn } from '../../lib/utils';
import { useDirty } from '../../lib/dirty';
import { APPROVAL_MODE_LONG, APPROVAL_MODE_SHORT, type ApprovalMode } from '@itsm/shared';

interface Response {
  userId: string;
  userName: string | null;
  onBehalfOf: string | null;
  onBehalfOfName: string | null;
  status: string;
  comment: string | null;
  decidedAt: number | null;
}
interface Stage {
  id: string;
  stage: number;
  mode: 'any' | 'all' | 'majority';
  status: string;
  responses: Response[];
}
interface Chain {
  chainId: string;
  status: string;
  stages: Stage[];
}

const CHAIN_BADGE: Record<string, string> = {
  pending: 'border-amber-500/40 text-amber-700 dark:text-amber-400',
  approved: 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400',
  rejected: 'border-destructive/40 text-destructive',
  cancelled: 'text-muted-foreground',
};

function StatusMark({ status }: { status: string }) {
  if (status === 'approved') return <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />;
  if (status === 'rejected') return <X className="h-3.5 w-3.5 text-destructive" />;
  if (status === 'cancelled') return <CircleSlash className="h-3.5 w-3.5 text-muted-foreground" />;
  return <Clock className="h-3.5 w-3.5 text-muted-foreground" />;
}

export function ApprovalsTab({ id, canEdit }: { id: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const { me } = useAuth();
  const [asking, setAsking] = useState(false);
  // URL state on purpose: this is a control on the request FORM — "who has
  // to agree" for the chain being composed — not a view of the ticket. In the
  // URL it would put a half-built request into a shared link.
  const [mode, setMode] = useState<'any' | 'all' | 'majority'>('any');
  const [approvers, setApprovers] = useState<string[]>([]);
  // Step 101 — ask a BOARD, or name people. A board carries its own stages,
  // so the mode and approver pickers stand down when one is chosen.
  const [boardId, setBoardId] = useState<string>('');
  const [note, setNote] = useState('');
  const [comment, setComment] = useState<Record<string, string>>({});
  // A typed note or an unsent approval comment is work. Without this,
  // switching tab throws it away silently.
  const clearDirty = useDirty(!!note.trim() || Object.values(comment).some((c) => c.trim()));

  const chains = useQuery({
    queryKey: qk.approvals.entity('ticket', id),
    queryFn: () => apiFetch<{ entries: Chain[] }>(`/api/approvals/entity/ticket/${id}`),
  });
  const boards = useQuery({
    queryKey: qk.approvalBoardPicker,
    queryFn: () =>
      apiFetch<{
        entries: {
          id: string;
          name: string;
          description: string | null;
          stages: { position: number; name: string | null; mode: string }[];
        }[];
      }>('/api/approvals/boards'),
    enabled: canEdit,
  });
  const people = useQuery({ queryKey: qk.people, queryFn: ticketsApi.people, staleTime: 5 * 60_000 });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.approvals.entity('ticket', id) });
    void qc.invalidateQueries({ queryKey: qk.approvals.mine });
    void qc.invalidateQueries({ queryKey: qk.tickets.activity(id) });
    /**
     * The TICKET too — Step 193.
     *
     * An approval decision is the one thing that unblocks a change leaving
     * Awaiting approval, and the stepper reads that from the ticket detail
     * (`changeApproval`), not from this tab's own query. Without this line the
     * decision landed here, the tab updated, and the stepper went on refusing
     * with stale data until the page was reloaded — Kai: *"Also tried a manual
     * approve, no avail."* Approving something and watching nothing happen is
     * how people conclude a feature is broken and stop using it.
     */
    void qc.invalidateQueries({ queryKey: qk.tickets.detail(id) });
  };

  const ask = useMutation({
    mutationFn: () =>
      apiSend('/api/approvals', 'POST', {
        entityType: 'ticket',
        entityId: id,
        note: note.trim() || null,
        ...(boardId ? { boardId } : { stages: [{ mode, approverIds: approvers }] }),
      }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not request that.');
      clearDirty();
      setAsking(false);
      setApprovers([]);
      setBoardId('');
      setNote('');
      refresh();
    },
  });
  const answer = useMutation({
    mutationFn: (v: { requestId: string; status: 'approved' | 'rejected' }) =>
      apiSend(`/api/approvals/${v.requestId}/decide`, 'POST', {
        status: v.status,
        comment: comment[v.requestId]?.trim() || null,
      }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not record that.');
      clearDirty();
      refresh();
    },
  });
  const cancel = useMutation({
    mutationFn: (chainId: string) => apiSend(`/api/approvals/chain/${chainId}/cancel`, 'POST'),
    onSuccess: () => refresh(),
  });
  const remind = useMutation({
    mutationFn: (chainId: string) =>
      apiSend<{ sent: boolean }>(`/api/approvals/chain/${chainId}/remind`, 'POST'),
    onSuccess: (r) =>
      // Honest about it: nothing is sent until Step 118.
      toast.message(
        r.ok && r.body?.sent === false
          ? 'Noted — reminder emails start once the notification model lands.'
          : 'Reminder sent.',
      ),
  });

  if (chains.isPending)
    return (
      <div className="p-4">
        <Skeleton className="h-24 w-full" />
      </div>
    );
  const entries = chains.data?.entries ?? [];

  return (
    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Approvals</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">Stages run in order.</p>
        </div>
        {canEdit && !asking && (
          <Button size="sm" variant="outline" onClick={() => setAsking(true)}>
            <ShieldCheck className="h-4 w-4" />
            Request approval
          </Button>
        )}
      </div>

      {asking && (
        <form
          className="space-y-3 rounded-md border p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (boardId || approvers.length) ask.mutate();
          }}
        >
          {(boards.data?.entries.length ?? 0) > 0 && (
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Board</label>
              <Select
                value={boardId}
                onChange={(e) => setBoardId(e.target.value)}
                aria-label="Approval board"
              >
                <option value="">Name the approvers myself</option>
                {(boards.data?.entries ?? []).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} ({b.stages.length} stage{b.stages.length === 1 ? '' : 's'})
                  </option>
                ))}
              </Select>
              {boardId && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {(boards.data?.entries.find((b) => b.id === boardId)?.stages ?? [])
                    .map(
                      (st, i) =>
                        `${i + 1}. ${st.name ?? 'stage ' + (i + 1)} (${APPROVAL_MODE_SHORT[st.mode as ApprovalMode]})`,
                    )
                    .join(' → ')}
                  . The people are worked out now and kept — editing the board later will not change this
                  approval.
                </p>
              )}
            </div>
          )}
          {!boardId && (
            <>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Approvers</label>
                {/* Searchable pills, not a `<select multiple>`: a multi-select
                listbox is the control people mis-click most, and a directory
                of hundreds cannot be scrolled to. */}
                <TokenSelect
                  value={approvers}
                  onChange={setApprovers}
                  ariaLabel="Approvers"
                  options={(people.data?.entries ?? []).map((p) => ({
                    value: p.id,
                    label: p.name ?? p.email,
                  }))}
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Who has to agree
                </label>
                <Select value={mode} onChange={(e) => setMode(e.target.value as 'any' | 'all' | 'majority')}>
                  <option value="any">Any one of them is enough</option>
                  <option value="majority">A majority of them must approve</option>
                  <option value="all">All of them must approve</option>
                </Select>
              </div>
            </>
          )}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Note <span className="font-normal">— optional</span>
            </label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => setAsking(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={(!boardId && !approvers.length) || ask.isPending}>
              {ask.isPending ? 'Requesting…' : 'Request'}
            </Button>
          </div>
        </form>
      )}

      {entries.length === 0 && !asking && (
        <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          Nothing has been sent for approval.
        </p>
      )}

      {entries.map((c) => (
        <section key={c.chainId} className="space-y-2 rounded-md border p-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={cn(CHAIN_BADGE[c.status])}>
              {c.status}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {c.stages.length} stage{c.stages.length === 1 ? '' : 's'}
            </span>
            {canEdit && c.status === 'pending' && (
              <div className="ml-auto flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => remind.mutate(c.chainId)}>
                  Remind
                </Button>
                <Button size="sm" variant="ghost" onClick={() => cancel.mutate(c.chainId)}>
                  Cancel
                </Button>
              </div>
            )}
          </div>
          {c.stages.map((s) => {
            const mine = s.responses.find((r) => r.userId === me?.user.id);
            const canAnswer = s.status === 'pending' && mine?.status === 'pending';
            return (
              <div key={s.id} className={cn('rounded border p-2', s.status === 'waiting' && 'opacity-60')}>
                <div className="mb-1 flex items-center gap-2 text-xs">
                  <StatusMark status={s.status} />
                  <span className="font-medium">Stage {s.stage}</span>
                  <span className="text-muted-foreground">{APPROVAL_MODE_LONG[s.mode as ApprovalMode]}</span>
                  <span className="ml-auto text-muted-foreground">{s.status}</span>
                </div>
                <ul className="space-y-0.5">
                  {s.responses.map((r) => (
                    <li key={r.userId} className="flex items-center gap-1.5 text-xs">
                      <StatusMark status={r.status} />
                      <span>{r.userName ?? r.userId}</span>
                      {/* Whose authority this used. An approval with no trace
                          of that is a hole in the audit trail. */}
                      {r.onBehalfOfName && (
                        <span className="text-muted-foreground">covering {r.onBehalfOfName}</span>
                      )}
                      {r.comment && <span className="truncate text-muted-foreground">— {r.comment}</span>}
                      {r.decidedAt && (
                        <span className="ml-auto shrink-0 text-muted-foreground">
                          {fullTime(r.decidedAt)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                {canAnswer && (
                  <div className="mt-2 flex items-center gap-2">
                    <Input
                      value={comment[s.id] ?? ''}
                      onChange={(e) => setComment((c2) => ({ ...c2, [s.id]: e.target.value }))}
                      placeholder="Comment (optional)"
                      className="h-8 flex-1"
                    />
                    <Button
                      size="sm"
                      onClick={() => answer.mutate({ requestId: s.id, status: 'approved' })}
                      disabled={answer.isPending}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => answer.mutate({ requestId: s.id, status: 'rejected' })}
                      disabled={answer.isPending}
                    >
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}
